import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useHeroCarousel } from "../../../hooks/useHeroCarousel";
import { translateStoreCategory, getStoreProductPriceDisplay } from "../../../utils/storeUtils";
import { StoreProductPriceBlock } from "./StoreProductPriceBlock";

/** Same breakpoint as mobile hero in `Store.css` */
const HERO_BG_MOBILE_MEDIA = "(max-width: 640px)";

function useResponsiveHeroSlideBgUrl(desktopUrl, mobileUrl) {
  const desktop = String(desktopUrl || "").trim();
  const mobile = String(mobileUrl || "").trim();

  const pick = React.useCallback(() => {
    if (typeof window === "undefined") return desktop || mobile;
    if (window.matchMedia(HERO_BG_MOBILE_MEDIA).matches && mobile) return mobile;
    return desktop || mobile;
  }, [desktop, mobile]);

  const [url, setUrl] = React.useState(pick);

  React.useEffect(() => {
    setUrl(pick());
    const mq = window.matchMedia(HERO_BG_MOBILE_MEDIA);
    const onChange = () => setUrl(pick());
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pick]);

  return url;
}

function categoryToLabel(cat) {
  return (cat || "").charAt(0).toUpperCase() + (cat || "").slice(1).toLowerCase();
}

/** Arabic script range — English slide copy stays LTR to avoid bidi punctuation bugs */
function hasArabicScript(text) {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(String(text || ""));
}

function HeroSlideContent({
  slide,
  products,
  isActive,
  onAddToCart,
  onCategoryClick,
  resolveUrl,
}) {
  const { t, i18n } = useTranslation("global");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");

  // Cycle through multiple products every 3.5s; reset when slide becomes inactive
  const [productSubIndex, setProductSubIndex] = React.useState(0);
  const [dotResetKey, setDotResetKey] = React.useState(0);
  React.useEffect(() => {
    if (!isActive) { setProductSubIndex(0); return; }
    if (!products || products.length <= 1) return;
    const timer = setInterval(() => {
      setProductSubIndex((i) => (i + 1) % products.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [isActive, products, dotResetKey]);

  const handleDotClick = React.useCallback((i) => {
    setProductSubIndex(i);
    setDotResetKey((k) => k + 1);
  }, []);

  const product = products?.[productSubIndex] || products?.[0] || null;

  // For Arabic: never rely on the slide's category (static hero uses `default`).
  // Use the featured product's category label when available so translation keys like `armchairs` work.
  const categoryRaw = product?.category || slide.category;
  const categoryCanonicalLabel = categoryToLabel(categoryRaw);
  const categoryExploreDisplay = translateStoreCategory(t, categoryRaw || categoryCanonicalLabel);
  const handleCategoryLink = (e) => {
    e.stopPropagation();
    onCategoryClick?.(categoryCanonicalLabel);
  };

  // Trust strip: use slide data when available, else fall back to i18n defaults
  const slideTrust = Array.isArray(slide.heroTrustItems) ? slide.heroTrustItems : [];
  const hasTrustData = slideTrust.length >= 3 && slideTrust.some((it) => (isRtl ? it.ar : it.en));
  const trustItems = hasTrustData
    ? slideTrust.slice(0, 3).map((it) => ({
        icon: it.icon || "fa-award",
        label: isRtl ? (it.ar || it.en) : (it.en || it.ar),
      }))
    : [
        { icon: "fa-award", label: t("store.heroTrustQuality", { defaultValue: "+15 Years Experience" }) },
        { icon: "fa-layer-group", label: t("store.heroTrustStyle", { defaultValue: "+500 Happy Clients" }) },
        { icon: "fa-headset", label: t("store.heroTrustCX", { defaultValue: "Delivery Nationwide" }) },
      ];

  const displayTitle = (isRtl ? slide.heroTitleAr : slide.heroTitleEn) || slide.title;
  const displaySubtitle = (isRtl ? slide.heroSubtitleAr : slide.heroSubtitleEn) || slide.subtitle;

  const textX = isRtl ? (slide.heroTextXAr !== undefined ? slide.heroTextXAr : slide.heroTextX || 0) : (slide.heroTextX || 0);
  const textY = isRtl ? (slide.heroTextYAr !== undefined ? slide.heroTextYAr : slide.heroTextY || 0) : (slide.heroTextY || 0);
  const btnX = isRtl ? (slide.heroBtnXAr !== undefined ? slide.heroBtnXAr : slide.heroBtnX || 0) : (slide.heroBtnX || 0);
  const btnY = isRtl ? (slide.heroBtnYAr !== undefined ? slide.heroBtnYAr : slide.heroBtnY || 0) : (slide.heroBtnY || 0);
  const btnMobileX = isRtl ? (slide.heroBtnMobileXAr !== undefined ? slide.heroBtnMobileXAr : slide.heroBtnMobileX || 0) : (slide.heroBtnMobileX || 0);
  const btnMobileY = isRtl ? (slide.heroBtnMobileYAr !== undefined ? slide.heroBtnMobileYAr : slide.heroBtnMobileY || 0) : (slide.heroBtnMobileY || 0);

  const heroProductItems = Array.isArray(slide.heroProductItems) ? slide.heroProductItems : [];

  return (
    <>
      <div className="hero-left-panel">
        <div
          className="hero-left-panel-inner"
          style={{
            "--hero-text-x": `${textX}%`,
            "--hero-text-y": `${textY}%`,
          }}
        >
          <h1
            className="hero-title-v2"
            dir={hasArabicScript(displayTitle) ? "rtl" : "ltr"}
            lang={hasArabicScript(displayTitle) ? "ar" : "en"}
          >
            {displayTitle}
          </h1>
          {displaySubtitle && (
            <p
              className="hero-subtitle-v2"
              dir={hasArabicScript(displaySubtitle) ? "rtl" : "ltr"}
              lang={hasArabicScript(displaySubtitle) ? "ar" : "en"}
            >
              {displaySubtitle}
            </p>
          )}
          <div
            className="hero-actions-block"
            style={{
              "--hero-btn-x": `${btnX}%`,
              "--hero-btn-y": `${btnY}%`,
              "--hero-btn-mobile-x": `${btnMobileX}%`,
              "--hero-btn-mobile-y": `${btnMobileY}%`,
            }}
          >
            <div className="hero-cta-row hero-cta-row-v2">
              <span className="hero-btn-shop-drop">
                <span className="hero-btn-shop-shell">
                  <button
                    type="button"
                    className="hero-btn-shop"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/store/category/furniture");
                    }}
                  >
                    {t("store.heroShopFurniture", { defaultValue: "Shop Furniture" })}
                  </button>
                </span>
              </span>
              {onCategoryClick && (
                <button
                  type="button"
                  className="hero-btn-secondary"
                  onClick={handleCategoryLink}
                  dir={isRtl ? "rtl" : "ltr"}
                  lang={isRtl ? "ar" : "en"}
                >
                  {t("store.heroExploreCategory", {
                    category: categoryExploreDisplay,
                    defaultValue: isRtl ? `استكشف ${categoryExploreDisplay}` : `Explore ${categoryExploreDisplay}`,
                  })}
                </button>
              )}
            </div>
            <div className="hero-trust-strip">
              {trustItems.map((item, idx) => (
                <span key={idx} className="hero-trust-item">
                  <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Product image column — always on the right (layout fixed for both languages) */}
      <div
        className={`hero-product-col${product?._id ? " hero-product-col--clickable" : ""}`}
        onClick={product?._id ? (e) => { e.stopPropagation(); navigate(`/store/product/${product._id}`); } : undefined}
        role={product?._id ? "link" : undefined}
        tabIndex={product?._id ? 0 : undefined}
        onKeyDown={product?._id ? (e) => { if (e.key === "Enter") navigate(`/store/product/${product._id}`); } : undefined}
        aria-label={product?._id ? (isRtl ? product.arName || product.name : product.name) : undefined}
      >
        <div className="hero-product-blob" />
        {products && products.length > 0 ? (
          <div className="hero-product-col-imgs">
            {products.map((p, pi) => {
              const item = heroProductItems[pi] || {};
              const x = isRtl ? (item.xAr || item.x || 0) : (item.x || 0);
              const y = isRtl ? (item.yAr || item.y || 0) : (item.y || 0);
              const sc = isRtl ? (item.scaleAr || item.scale || 1) : (item.scale || 1);
              const mx = isRtl ? (item.mobileXAr || item.mobileX || 0) : (item.mobileX || 0);
              const my = isRtl ? (item.mobileYAr || item.mobileY || 0) : (item.mobileY || 0);
              const ms = isRtl ? (item.mobileScaleAr || item.mobileScale || 1) : (item.mobileScale || 1);
              const firstImg = p?.images?.[0];
              const rawUrl = item.imageUrl || (pi === 0 ? slide.productImageUrl : "") ||
                firstImg?.noBgUrl || firstImg?.url || p?.imageUrl || "";
              const imgUrl = rawUrl && resolveUrl ? resolveUrl(rawUrl) : rawUrl;
              return imgUrl ? (
                <img
                  key={p?._id || pi}
                  className={`hero-product-col-img${pi === productSubIndex ? " hero-product-col-img--active" : ""}`}
                  src={imgUrl}
                  alt={
                    (isRtl ? firstImg?.arAlt || firstImg?.alt : firstImg?.alt || firstImg?.arAlt) ||
                    (isRtl ? p?.arName || p?.name : p?.name || p?.arName) ||
                    (isRtl ? "منتج" : "Product")
                  }
                  draggable="false"
                  fetchpriority={pi === 0 ? "high" : undefined}
                  loading="eager"
                  style={{
                    "--item-x": `${x}%`,
                    "--item-y": `${y}%`,
                    "--item-scale": sc,
                    "--item-mobile-x": `${mx}%`,
                    "--item-mobile-y": `${my}%`,
                    "--item-mobile-scale": ms,
                  }}
                />
              ) : null;
            })}
          </div>
        ) : null}
        {products && products.length > 1 && (
          <div
            className="hero-product-dots"
            style={{
              "--hero-dots-x": `${slide.heroDotsX || 0}%`,
              "--hero-dots-y": `${slide.heroDotsY != null ? slide.heroDotsY : 16}px`,
              "--hero-dots-mobile-x": `${slide.heroDotsMobileX || 0}%`,
              "--hero-dots-mobile-y": `${slide.heroDotsMobileY != null ? slide.heroDotsMobileY : 10}px`,
            }}
          >
            {products.map((_, pi) => (
              <button
                key={pi}
                type="button"
                className={`hero-product-dot${
                  pi === productSubIndex
                    ? " hero-product-dot--active"
                    : pi < productSubIndex
                    ? " hero-product-dot--done"
                    : ""
                }`}
                onClick={(e) => { e.stopPropagation(); handleDotClick(pi); }}
                aria-label={`Product ${pi + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function HeroSlideArticle({
  slide,
  index,
  heroIndex,
  products,
  onAddToCart,
  onCategoryClick,
  resolveUrl,
}) {
  const { i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const desktopBg = (isRtl && slide.backgroundAr) ? slide.backgroundAr : slide.background;
  const mobileBg = (isRtl && slide.backgroundArMobile) ? slide.backgroundArMobile
    : (isRtl && slide.backgroundAr) ? slide.backgroundAr
    : slide.backgroundMobile;
  const rawBgUrl = useResponsiveHeroSlideBgUrl(desktopBg, mobileBg);
  const bgUrl = (resolveUrl && rawBgUrl) ? resolveUrl(rawBgUrl) : rawBgUrl;
  const isActive = index === heroIndex;

  return (
    <article
      className={`hero-slide hero-slide-v2 hero-theme-${slide.theme}${
        isActive ? " hero-slide-active" : ""
      }${bgUrl ? " hero-slide-v2--has-bg" : ""}`}
      aria-hidden={!isActive}
      aria-label={`Slide: ${slide.title}`}
    >
      {bgUrl && (
        <img
          className="hero-slide-bg-photo"
          src={bgUrl}
          alt=""
          aria-hidden="true"
          draggable="false"
          fetchpriority="high"
          loading="eager"
        />
      )}
      <HeroSlideContent
        slide={slide}
        products={products}
        isActive={isActive}
        onAddToCart={onAddToCart}
        onCategoryClick={onCategoryClick}
        resolveUrl={resolveUrl}
      />
    </article>
  );
}

export default function HeroCarousel({
  slides,
  products,
  onAddToCart,
  onCategoryClick,
  staticHero = false,
  resolveUrl,
}) {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const isStatic = staticHero || (Array.isArray(slides) ? slides.length <= 1 : true);
  const safeSlides = Array.isArray(slides) ? slides : [];
  const effectiveSlides = isStatic ? safeSlides.slice(0, 1) : safeSlides;
  const carouselRef = useRef(null);
  const {
    heroIndex,
    setHeroIndex,
    goPrev,
    goNext,
    isDragging,
    progress,
    setIsPaused,
    handlers,
    trackStyle,
  } = useHeroCarousel(effectiveSlides.length);



  React.useEffect(() => {
    if (isStatic) return;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goPrev, goNext, isStatic]);

  return (
    <section
      className="hero-carousel-wrap"
      aria-label={isStatic ? "Store hero" : "Store hero carousel"}
      dir={isRtl ? "rtl" : "ltr"}
      lang={isRtl ? "ar" : "en"}
    >
      <div
        ref={carouselRef}
        className="hero-carousel"
        {...(!isStatic ? handlers : {})}
        style={!isStatic ? { cursor: isDragging ? "grabbing" : "grab" } : undefined}
        onMouseEnter={!isStatic ? () => setIsPaused(true) : undefined}
        onMouseLeave={!isStatic ? () => setIsPaused(false) : undefined}
      >
        <div className="hero-silk-bg" aria-hidden="true" />
        <div className="hero-track" style={trackStyle}>
          {effectiveSlides.map((slide, index) => (
            <HeroSlideArticle
              key={`${slide.category}-${index}`}
              slide={slide}
              index={index}
              heroIndex={heroIndex}
              products={products}
              onAddToCart={onAddToCart}
              onCategoryClick={onCategoryClick}
              resolveUrl={resolveUrl}
            />
          ))}
        </div>

        {!isStatic ? (
          <>
            <button
              type="button"
              className="hero-nav prev"
              onClick={(e) => {
                e.stopPropagation();
                if (isRtl) goNext();
                else goPrev();
              }}
              aria-label={
                isRtl
                  ? t("store.heroNavNext", { defaultValue: "Next slide" })
                  : t("store.heroNavPrev", { defaultValue: "Previous slide" })
              }
            >
              <i
                className={`fa-solid ${isRtl ? "fa-angle-right" : "fa-angle-left"}`}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="hero-nav next"
              onClick={(e) => {
                e.stopPropagation();
                if (isRtl) goPrev();
                else goNext();
              }}
              aria-label={
                isRtl
                  ? t("store.heroNavPrev", { defaultValue: "Previous slide" })
                  : t("store.heroNavNext", { defaultValue: "Next slide" })
              }
            >
              <i
                className={`fa-solid ${isRtl ? "fa-angle-left" : "fa-angle-right"}`}
                aria-hidden="true"
              />
            </button>

            <div className="hero-dots-wrap">
              <div className="hero-dots" role="tablist" aria-label="Carousel slides">
                {effectiveSlides.map((_, dot) => (
                  <button
                    key={dot}
                    type="button"
                    role="tab"
                    aria-selected={heroIndex === dot}
                    aria-label={`Go to slide ${dot + 1}`}
                    className={heroIndex === dot ? "active" : ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHeroIndex(dot);
                    }}
                  />
                ))}
              </div>
              <div className="hero-dots-progress" aria-hidden="true">
                <div
                  className="hero-dots-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
